import { TextAlign } from "@tiptap/extension-text-align";
import type { ResolvedPos } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";

const HARD_BREAK = "hardBreak";

/** Position of the last hard break in `$pos`'s textblock that sits before it, if any. */
const hardBreakBefore = ($pos: ResolvedPos): number | null => {
  if (!$pos.parent.isTextblock) return null;
  let found: number | null = null;
  $pos.parent.forEach((child, offset) => {
    const pos = $pos.start() + offset;
    if (child.type.name === HARD_BREAK && pos < $pos.pos) found = pos;
  });
  return found;
};

/** Position of the first hard break in `$pos`'s textblock at or after it, if any. */
const hardBreakAfter = ($pos: ResolvedPos): number | null => {
  if (!$pos.parent.isTextblock) return null;
  let found: number | null = null;
  $pos.parent.forEach((child, offset) => {
    const pos = $pos.start() + offset;
    if (found === null && child.type.name === HARD_BREAK && pos >= $pos.pos) found = pos;
  });
  return found;
};

/**
 * Turns the hard breaks either side of the selection into block splits, so the
 * rows the selection covers become blocks of their own. Both halves of a split
 * keep the original block's attributes, so the other rows keep their alignment.
 */
export const isolateSelectedRows = (tr: Transaction): Transaction => {
  const { $from, $to } = tr.selection;
  const before = hardBreakBefore($from);
  const after = hardBreakAfter($to);
  // The later split first, so `before` is still a valid position.
  for (const pos of [after, before]) {
    if (pos === null) continue;
    tr.delete(pos, pos + 1);
    tr.split(pos);
  }
  return tr;
};

/**
 * `TextAlign` that aligns only the rows the selection is on. Plain TextAlign sets
 * `text-align` on the whole block, so in a paragraph built from `<br>` lines
 * (common in templates written as hand HTML) one click aligned every line.
 * This splits such a paragraph at the breaks around the selection first.
 * Covers the toolbar and the Mod-Shift-L/E/R/J shortcuts.
 */
export const RowTextAlign = TextAlign.extend({
  addCommands() {
    const parent = this.parent?.();
    return {
      ...parent,
      setTextAlign: (alignment: string) => (props) => {
        if (props.dispatch) {
          isolateSelectedRows(props.tr);
          // The chainable state caches doc and selection until `tr` is read; refresh
          // them so the alignment lands on the split rows, not the old positions.
          void props.state.tr;
        }
        return parent?.setTextAlign?.(alignment)(props) ?? false;
      },
    };
  },
});
